
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Leaf, ArrowLeft, Loader2, Check } from "lucide-react";

import LibraryPanel, { mapRawEpdToNormalized } from "./lca/LibraryPanel";
import CategorySection from "./lca/CategorySection";
import ResultsPanel from "./lca/ResultsPanel";

import { LCA_CATEGORIES, clampNumber, makeId } from "../lca/lcaTypes";
import { computeProject } from "../lca/lcaEngine";

const STORAGE_KEY = "embodix_lca_mvp_state_v1";
const EPD_LIBRARY_STORAGE_KEY = "embodix_lca_epd_library_v1";
const CUSTOM_EPD_LIBRARY_KEY = "embodix_custom_epds_v1";
const RHINO_LAYERS_KEY = "embodix_rhino_layers_v1";
const RHINO_LAYER_CATEGORIES_KEY = "embodix_rhino_layer_categories_v1";
const DASHBOARD_STORAGE_KEY = "embodix_dashboard_state_v1";
const RHINO_IMPACT_CATEGORIES = [
  { id: "gwp", name: "Global Warming Potential", unit: "kg CO2-eq", shortName: "GWP" }
];
const LAYER_CATEGORY_OPTIONS = [
  { value: "structure", label: "Structure" },
  { value: "envelope", label: "Envelope" },
  { value: "design", label: "Design" },
  { value: "mechanical", label: "Mechanical" },
  { value: "energy", label: "Energy - B6" }
];

function safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function mergeCustomEpds(existing, customRaw) {
  if (!Array.isArray(customRaw) || customRaw.length === 0) return existing || [];
  const merged = Array.isArray(existing) ? [...existing] : [];
  const indexById = new Map();
  merged.forEach((item, idx) => {
    if (item?.id) indexById.set(item.id, idx);
  });
  customRaw.forEach((raw) => {
    const normalized = mapRawEpdToNormalized(raw);
    if (!normalized?.id) return;
    const index = indexById.get(normalized.id);
    if (typeof index === "number") {
      merged[index] = { ...merged[index], ...normalized };
    } else {
      indexById.set(normalized.id, merged.length);
      merged.push(normalized);
    }
  });
  return merged;
}

export default function LCAToolPage() {
  const navigate = useNavigate();
  const [buildingLifeYears, setBuildingLifeYears] = useState(60);
  const [buildingArea, setBuildingArea] = useState(1000);
  const [includeModuleD, setIncludeModuleD] = useState(true);
  const [includeModuleB6, setIncludeModuleB6] = useState(true);
  const [countReplacementEndOfLife, setCountReplacementEndOfLife] = useState(true);
  const [activeView, setActiveView] = useState("collect");
  const [hydrated, setHydrated] = useState(false);

  const [epdLibrary, setEpdLibrary] = useState([]);
  const [items, setItems] = useState([]);
  const [layerMatcherRows, setLayerMatcherRows] = useState([]);
  const [layerCategoryMap, setLayerCategoryMap] = useState({});
  const [layerSaveState, setLayerSaveState] = useState("idle");
  const [layerSaveNotice, setLayerSaveNotice] = useState("");
  const layerMatcherAutoOpenedRef = useRef(false);
  const [layerMatcherHydrated, setLayerMatcherHydrated] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [buildSaveState, setBuildSaveState] = useState("idle");
  const [buildDirty, setBuildDirty] = useState(false);
  const buildDirtyInitRef = useRef(false);

  const epdsById = useMemo(() => {
    const map = {};
    for (const e of epdLibrary || []) map[e.id] = e;
    return map;
  }, [epdLibrary]);

  // Load persisted state
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const data = safeJsonParse(raw);
    if (!data) return;

    if (Number.isFinite(Number(data.buildingLifeYears))) setBuildingLifeYears(Number(data.buildingLifeYears));
    if (Number.isFinite(Number(data.buildingArea))) setBuildingArea(Number(data.buildingArea));
    if (typeof data.includeModuleD === "boolean") setIncludeModuleD(data.includeModuleD);
    if (typeof data.includeModuleB6 === "boolean") setIncludeModuleB6(data.includeModuleB6);
    if (typeof data.countReplacementEndOfLife === "boolean") setCountReplacementEndOfLife(data.countReplacementEndOfLife);

    const storedLibrary = safeJsonParse(localStorage.getItem(EPD_LIBRARY_STORAGE_KEY));
    const baseLibrary = Array.isArray(data.epdLibrary)
      ? data.epdLibrary
      : Array.isArray(storedLibrary)
        ? storedLibrary
        : [];
    const customRaw = safeJsonParse(localStorage.getItem(CUSTOM_EPD_LIBRARY_KEY)) || [];
    setEpdLibrary(mergeCustomEpds(baseLibrary, customRaw));
    if (Array.isArray(data.items)) setItems(data.items);
    setHydrated(true);
  }, []);

  useEffect(() => {
    const applyDashboardBuildingInfo = () => {
      const rawDashboard = safeJsonParse(localStorage.getItem(DASHBOARD_STORAGE_KEY));
      const info = rawDashboard?.lcaData?.buildingInfo;
      if (!info) return;
      const nextLife = Number(info.serviceLife);
      const nextArea = Number(info.area);
      if (Number.isFinite(nextLife)) setBuildingLifeYears(nextLife);
      if (Number.isFinite(nextArea)) setBuildingArea(nextArea);
    };

    applyDashboardBuildingInfo();
    const handleStorage = (event) => {
      if (event.key === DASHBOARD_STORAGE_KEY) {
        applyDashboardBuildingInfo();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    const rawLayers = safeJsonParse(localStorage.getItem(RHINO_LAYERS_KEY));
    if (Array.isArray(rawLayers)) {
      const normalized = rawLayers.map((item, index) => ({
        id: item?.layer ? `${item.layer}-${index}` : `layer_${index}`,
        layer: item?.layer ? String(item.layer) : `Layer ${index + 1}`,
        declaration: item?.declaration ? String(item.declaration) : "N/A",
        quantity: Number.isFinite(Number(item?.quantity)) ? Number(item.quantity) : "",
      }));
      setLayerMatcherRows(normalized);
      if (normalized.length && activeView === "build" && !layerMatcherAutoOpenedRef.current) {
        layerMatcherAutoOpenedRef.current = true;
        setActiveView("layer-matcher");
      }
    }
    const savedCategories = safeJsonParse(localStorage.getItem(RHINO_LAYER_CATEGORIES_KEY));
    if (savedCategories && typeof savedCategories === "object") {
      setLayerCategoryMap(savedCategories);
    }
    setLayerMatcherHydrated(true);
  }, []);

  useEffect(() => {
    if (hydrated) return;
    const customRaw = safeJsonParse(localStorage.getItem(CUSTOM_EPD_LIBRARY_KEY)) || [];
    if (customRaw.length) {
      setEpdLibrary((prev) => mergeCustomEpds(prev, customRaw));
    }
  }, [hydrated]);

  // Persist state
  useEffect(() => {
    if (!hydrated) {
      // Avoid clobbering stored data on first mount before hydration
      return;
    }
    const payload = {
      buildingLifeYears,
      buildingArea,
      includeModuleD,
      includeModuleB6,
      countReplacementEndOfLife,
      epdLibrary,
      items
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    localStorage.setItem(EPD_LIBRARY_STORAGE_KEY, JSON.stringify(epdLibrary || []));
  }, [buildingLifeYears, buildingArea, includeModuleD, includeModuleB6, countReplacementEndOfLife, epdLibrary, items, hydrated]);

  useEffect(() => {
    if (!layerMatcherHydrated) return;
    localStorage.setItem(RHINO_LAYER_CATEGORIES_KEY, JSON.stringify(layerCategoryMap));
  }, [layerCategoryMap, layerMatcherHydrated]);

  useEffect(() => {
    if (!layerMatcherHydrated || layerMatcherRows.length === 0) return;
    localStorage.setItem(RHINO_LAYERS_KEY, JSON.stringify(layerMatcherRows));
  }, [layerMatcherRows, layerMatcherHydrated]);

  useEffect(() => {
    if (!buildDirtyInitRef.current) {
      buildDirtyInitRef.current = true;
      return;
    }
    setBuildDirty(true);
    setBuildSaveState("idle");
  }, [items]);

  // If there was nothing to hydrate, mark as ready so future changes persist
  useEffect(() => {
    if (!hydrated) setHydrated(true);
  }, [hydrated]);

  const settings = useMemo(() => {
    return {
      buildingLifeYears: clampNumber(buildingLifeYears, 60),
      buildingArea: clampNumber(buildingArea, 0),
      includeModuleD,
      includeModuleB6,
      countReplacementEndOfLife
    };
  }, [buildingLifeYears, buildingArea, includeModuleD, includeModuleB6, countReplacementEndOfLife]);

  const computedProject = useMemo(() => {
    return computeProject({
      items,
      epdsById,
      settings
    });
  }, [items, epdsById, settings]);
  const resultsReady = useMemo(() => {
    const entries = Object.values(computedProject?.byItemId || {});
    return entries.some((comp) => comp?.ok);
  }, [computedProject]);
  const hasRhinoLayers = layerMatcherRows.length > 0;
  const rhinoDashboardPayload = useMemo(() => {
    if (!hasRhinoLayers) return null;
    const byItem = computedProject?.byItemId || {};
    const rhinoItems = (items || []).filter((item) => item?.source === "rhino");
    if (!rhinoItems.length) return null;
    const rhinoOkCount = rhinoItems.filter((item) => byItem[item.id]?.ok).length;
    if (rhinoOkCount !== rhinoItems.length) return null;

    const impactCategories = RHINO_IMPACT_CATEGORIES;
    const categoryLabelMap = new Map(LCA_CATEGORIES.map((c) => [c.key, c.label]));

    const components = (items || [])
      .filter((item) => byItem[item.id]?.ok)
      .map((item) => {
        const total = clampNumber(byItem[item.id]?.total, 0);
        const impacts = {};
        impactCategories.forEach((cat) => {
          impacts[cat.id] = cat.id === "gwp" ? total : 0;
        });
        return {
          id: item.id,
          name: item.name || "Component",
          category: categoryLabelMap.get(item.category) || item.category || "Other",
          material: item.subcategory || item.name || "Material",
          quantity: Number(item.quantity) || 0,
          unit: item.quantityUnit || "m3",
          impacts
        };
      });

    if (components.length === 0) return null;

    const lcaStagesData = {};
    const emptyStages = () => ({
      A1: 0,
      A2: 0,
      A3: 0,
      A4: 0,
      A5: 0,
      B4: 0,
      B6: 0,
      C1: 0,
      C2: 0,
      C3: 0,
      C4: 0,
      D: 0
    });

    (items || []).forEach((item) => {
      if (!byItem[item.id]?.ok) return;
      const stageImpacts = byItem[item.id]?.stageImpacts || {};
      const stages = emptyStages();
      const a1a3 = clampNumber(stageImpacts.A1A3, 0);
      stages.A1 = a1a3;
      stages.A4 = clampNumber(stageImpacts.A4, 0);
      stages.A5 = clampNumber(stageImpacts.A5, 0);
      stages.B4 = clampNumber(stageImpacts.B4, 0);
      stages.B6 = clampNumber(stageImpacts.B6, 0);
      const c1c4 = clampNumber(stageImpacts.C1C4, 0);
      stages.C1 = c1c4;
      const d = Number(stageImpacts.D);
      stages.D = Number.isFinite(d) ? d : 0;
      lcaStagesData[item.id] = {
        component: item.name || "Component",
        ...stages
      };
    });

    const totalImpacts = {};
    impactCategories.forEach((cat) => {
      totalImpacts[cat.id] = components.reduce((sum, c) => sum + (c.impacts?.[cat.id] || 0), 0);
    });

    const area = Number(buildingArea);
    const life = Number(buildingLifeYears);
    const energyItem = (items || []).find((item) => item?.category === "energy");
    const energyUse = Number(energyItem?.energyUseKwhPerM2Yr);
    const buildingInfo = {
      name: "Rhino Import",
      area: Number.isFinite(area) && area > 0 ? area : 1000,
      location: "Unknown",
      serviceLife: Number.isFinite(life) && life > 0 ? life : 60,
      buildingType: "office",
      energyUseKwhPerM2Yr: Number.isFinite(energyUse) && energyUse > 0 ? energyUse : ""
    };

    return {
      buildingInfo,
      impactCategories,
      components,
      totalImpacts,
      lcaStagesData,
      projectId: `project_rhino_${Date.now()}`,
      projectName: buildingInfo.name,
      description: `Rhino integration LCA for ${buildingInfo.name}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isRhinoImport: true
    };
  }, [hasRhinoLayers, items, computedProject, buildingArea, buildingLifeYears]);

  const addItem = (item) => setItems((prev) => [item, ...(prev || [])]);

  const updateItem = (updated) => {
    setItems((prev) => (prev || []).map((x) => (x.id === updated.id ? updated : x)));
  };

  const removeItem = (id) => {
    setItems((prev) => (prev || []).filter((x) => x.id !== id));
  };

  const clearAll = () => {
    setItems([]);
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? safeJsonParse(raw) : null;
    if (!parsed) return;
    const next = { ...parsed, items: [] };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const handleBuildSave = () => {
    setBuildSaveState("saving");
    setBuildDirty(false);
    if (rhinoDashboardPayload) {
      const storedDashboard = safeJsonParse(localStorage.getItem(DASHBOARD_STORAGE_KEY));
      const storedInfo = storedDashboard?.lcaData?.buildingInfo;
      const shouldMergeStored =
        storedInfo &&
        typeof storedInfo === "object" &&
        String(storedInfo.location || "").toLowerCase() !== "unknown";
      const mergeStoredBuildingInfo = (baseInfo, incoming) => {
        if (!incoming || typeof incoming !== "object") return baseInfo;
        const fields = ["name", "area", "location", "serviceLife", "buildingType"];
        const overrides = {};
        fields.forEach((field) => {
          const value = incoming[field];
          if (value !== undefined && value !== null && String(value).trim() !== "") {
            overrides[field] = value;
          }
        });
        return { ...baseInfo, ...overrides };
      };
      const mergedPayload = shouldMergeStored
        ? {
            ...rhinoDashboardPayload,
            buildingInfo: mergeStoredBuildingInfo(rhinoDashboardPayload.buildingInfo, storedInfo)
          }
        : rhinoDashboardPayload;
      localStorage.setItem(
        DASHBOARD_STORAGE_KEY,
        JSON.stringify({
          lcaData: mergedPayload,
          requireBuildingInfoCompletion: false,
          preferLcaTabFirst: true
        })
      );
    }
    window.setTimeout(() => setBuildSaveState("saved"), 500);
  };

  const seedExample = () => {
    // Adds 4 placeholder components so you can test UI quickly
    setItems((prev) => {
      const now = Date.now();
      const base = [
        {
          id: makeId(`seed_${now}_1`),
          category: "structure",
          subcategory: "Concrete",
          name: "Foundations",
          quantity: "50",
          quantityUnit: "m3",
          serviceLifeYears: "60",
          densityKgPerM3: "2400",
          thicknessM: "",
          customFactor: "",
          epdId: ""
        },
        {
          id: makeId(`seed_${now}_2`),
          category: "structure",
          subcategory: "Steel",
          name: "Columns",
          quantity: "8",
          quantityUnit: "t",
          serviceLifeYears: "60",
          densityKgPerM3: "",
          thicknessM: "",
          customFactor: "",
          epdId: ""
        },
        {
          id: makeId(`seed_${now}_3`),
          category: "envelope",
          subcategory: "Facade",
          name: "External wall cladding",
          quantity: "1200",
          quantityUnit: "m2",
          serviceLifeYears: "30",
          densityKgPerM3: "",
          thicknessM: "0.02",
          customFactor: "",
          epdId: ""
        },
        {
          id: makeId(`seed_${now}_4`),
          category: "mechanical",
          subcategory: "HVAC",
          name: "Chiller",
          quantity: "1",
          quantityUnit: "pcs",
          serviceLifeYears: "20",
          densityKgPerM3: "",
          thicknessM: "",
          customFactor: "1",
          epdId: ""
        },
        {
          id: makeId(`seed_${now}_5`),
          category: "design",
          subcategory: "Interior",
          name: "Interior walls component",
          quantity: "300",
          quantityUnit: "m2",
          serviceLifeYears: "30",
          densityKgPerM3: "",
          thicknessM: "0.12",
          customFactor: "",
          epdId: ""
        }
      ];
      return [...base, ...(prev || [])];
    });
  };

  const groupedByCategory = useMemo(() => {
    const map = {};
    for (const c of LCA_CATEGORIES) map[c.key] = [];
    for (const it of items || []) {
      const k = it.category || "structure";
      if (!map[k]) map[k] = [];
      map[k].push(it);
    }
    return map;
  }, [items]);

  const handleLayerCategoryChange = (rowId, value) => {
    setLayerCategoryMap((prev) => ({
      ...prev,
      [rowId]: value
    }));
    setLayerSaveState("idle");
    setLayerSaveNotice("");
  };

  const getQuantityUnit = (declaration) => {
    const normalized = String(declaration || "").toLowerCase();
    if (normalized.includes("volume")) return "m3";
    if (normalized.includes("area")) return "m2";
    if (normalized.includes("length")) return "m";
    if (normalized.includes("count")) return "pcs";
    return "m2";
  };

  const handleSaveLayerMatcher = () => {
    const unassignedCount = layerMatcherRows.filter((row) => !layerCategoryMap[row.id]).length;
    const layerItems = layerMatcherRows
      .filter((row) => layerCategoryMap[row.id])
      .map((row) => ({
        id: `rhino_${row.id}`,
        source: "rhino",
        layerId: row.id,
        category: layerCategoryMap[row.id],
        subcategory: "Rhino layer",
        name: row.layer,
        quantity: row.quantity !== "" ? String(row.quantity) : "",
        quantityUnit: getQuantityUnit(row.declaration),
        serviceLifeYears: "60",
        densityKgPerM3: "",
        thicknessM: "",
        customFactor: "",
        epdId: ""
      }));

    setItems((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const nonRhino = existing.filter((item) => item?.source !== "rhino");
      return [...layerItems, ...nonRhino];
    });
    localStorage.setItem(RHINO_LAYERS_KEY, JSON.stringify(layerMatcherRows));
    localStorage.setItem(RHINO_LAYER_CATEGORIES_KEY, JSON.stringify(layerCategoryMap));
    setLayerSaveState("saving");
    if (unassignedCount > 0) {
      setLayerSaveNotice("Unassigned categories were not considered.");
    } else {
      setLayerSaveNotice("");
    }
    window.setTimeout(() => setLayerSaveState("saved"), 800);
  };

  useEffect(() => {
    if (!hasRhinoLayers && activeView === "layer-matcher") {
      setActiveView("collect");
    }
    if (!resultsReady && activeView === "results") {
      setActiveView("build");
    }
  }, [activeView, hasRhinoLayers, resultsReady]);

  useEffect(() => {
    if (!rhinoDashboardPayload) return;
    const storedDashboard = safeJsonParse(localStorage.getItem(DASHBOARD_STORAGE_KEY));
    const storedInfo = storedDashboard?.lcaData?.buildingInfo;
    const shouldMergeStored =
      storedInfo &&
      typeof storedInfo === "object" &&
      String(storedInfo.location || "").toLowerCase() !== "unknown";
    const mergeStoredBuildingInfo = (baseInfo, incoming) => {
      if (!incoming || typeof incoming !== "object") return baseInfo;
      const fields = ["name", "area", "location", "serviceLife", "buildingType"];
      const overrides = {};
      fields.forEach((field) => {
        const value = incoming[field];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          overrides[field] = value;
        }
      });
      return { ...baseInfo, ...overrides };
    };
    const mergedPayload = shouldMergeStored
      ? {
          ...rhinoDashboardPayload,
          buildingInfo: mergeStoredBuildingInfo(rhinoDashboardPayload.buildingInfo, storedInfo)
        }
      : rhinoDashboardPayload;
    localStorage.setItem(
      DASHBOARD_STORAGE_KEY,
      JSON.stringify({
        lcaData: mergedPayload,
        requireBuildingInfoCompletion: false,
        preferLcaTabFirst: true
      })
    );
  }, [rhinoDashboardPayload]);

  return (
    <div className="min-h-screen bg-[#e9ede3]">
      <header className="border-b border-[#d5dacc] bg-[#e9ede3]">
        <div className="mx-auto w-full max-w-[110rem] px-4 sm:px-6 lg:px-8 py-4">
          <div className="relative grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-2">
            <div className="col-start-1 row-start-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[#2b4331] hover:text-[#245040] px-2"
                aria-label="Back to dashboard"
                onClick={() => navigate("/dashboard")}
              >
                <ArrowLeft className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Back to dashboard</span>
              </Button>
            </div>

            <div className="absolute left-1/2 top-0 -translate-x-1/2 sm:hidden">
              <span className="inline-flex items-center justify-center rounded-full border border-[#d5dacc] bg-white/70 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.25em] text-[#2b4331]">
                EmbodiX
              </span>
            </div>

            <div className="col-start-3 row-start-1 hidden sm:block w-[140px]" aria-hidden />

            <div className="col-span-3 row-start-2 sm:col-start-2 sm:col-span-1 sm:row-start-1 flex flex-col items-center text-center gap-1 sm:gap-2">
              <span className="hidden sm:inline-flex items-center justify-center rounded-full border border-[#d5dacc] bg-white/70 px-3 py-1 text-xs font-medium uppercase tracking-[0.25em] text-[#2b4331]">
                EmbodiX
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <h1 className="text-lg sm:text-xl font-semibold text-[#2b4331]">Whole building LCA</h1>
                <Badge
                  variant="secondary"
                  className="border border-[#d5dacc] bg-white/70 text-[#2b4331] shadow-sm"
                >
                  Beta
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[110rem] px-4 sm:px-6 lg:px-8 pb-10 space-y-6">
        <Card className="border border-[#d5dacc] bg-white shadow-sm rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Leaf className="w-5 h-5 text-[#2f6046]" />
              <span className="text-[#2b4331]">LCA tool settings</span>
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <div className="text-xs text-[#4f6b5a]">Building life (years)</div>
                <Input
                  value={buildingLifeYears}
                  onChange={(e) => setBuildingLifeYears(e.target.value)}
                  className="h-8 w-[160px]"
                  inputMode="decimal"
                  disabled
                />
              </div>

              <div className="space-y-1">
                <div className="text-xs text-[#4f6b5a]">Building area (m²)</div>
                <Input
                  value={buildingArea}
                  onChange={(e) => setBuildingArea(e.target.value)}
                  className="h-8 w-[160px]"
                  inputMode="decimal"
                  disabled
                />
              </div>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-[#2b4331] select-none">
                  <input
                    type="checkbox"
                    checked={includeModuleD}
                    onChange={(e) => setIncludeModuleD(e.target.checked)}
                    className="accent-[#2f6046]"
                  />
                  Include module D
                </label>
                <Badge
                  variant="secondary"
                  className="text-[10px] border border-[#d5dacc] bg-[#eef2e7] text-[#2b4331]"
                >
                  beyond boundary
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-[#2b4331] select-none">
                  <input
                    type="checkbox"
                    checked={includeModuleB6}
                    onChange={(e) => setIncludeModuleB6(e.target.checked)}
                    className="accent-[#2f6046]"
                  />
                  Include B6 (energy) module
                </label>
              </div>

            <div className="ml-auto flex flex-wrap gap-2">
              {!hasRhinoLayers && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 border-[#d5dacc] bg-white text-[#2b4331] hover:bg-[#eef2e7]"
                    onClick={seedExample}
                  >
                    Load template
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 border-[#d5dacc] bg-white text-[#2b4331] hover:bg-[#eef2e7]"
                    onClick={() => setClearConfirmOpen(true)}
                  >
                    Clear
                  </Button>
                </>
              )}
            </div>
            </div>

            <div className="text-sm text-[#4f6b5a]">
              Build components under each category, select EPDs from your shortlisted library, and review the full A1 to D GWP results.
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeView} onValueChange={setActiveView} className="space-y-6">
          <div className="flex flex-col items-start gap-2 text-left">
            <TabsList className="bg-white border border-[#d5dacc] rounded-xl overflow-hidden divide-x divide-[#d5dacc] p-0">
              {hasRhinoLayers && (
                <TabsTrigger
                  value="layer-matcher"
                  className="rounded-none first:rounded-l-xl last:rounded-r-xl px-4 py-2 text-sm font-medium text-[#2b4331] hover:bg-[#eef2e7] data-[state=active]:bg-[#2f6046] data-[state=active]:text-white"
                >
                  Layer Matcher
                </TabsTrigger>
              )}
              <TabsTrigger
                value="collect"
                className="rounded-none first:rounded-l-xl last:rounded-r-xl px-4 py-2 text-sm font-medium text-[#2b4331] hover:bg-[#eef2e7] data-[state=active]:bg-[#2f6046] data-[state=active]:text-white"
              >
                Collect Data
              </TabsTrigger>
              <TabsTrigger
                value="build"
                className="rounded-none first:rounded-l-xl last:rounded-r-xl px-4 py-2 text-sm font-medium text-[#2b4331] hover:bg-[#eef2e7] data-[state=active]:bg-[#2f6046] data-[state=active]:text-white"
              >
                Build
              </TabsTrigger>
              <TabsTrigger
                value="results"
                disabled={!resultsReady}
                className="rounded-none first:rounded-l-xl last:rounded-r-xl px-4 py-2 text-sm font-medium text-[#2b4331] hover:bg-[#eef2e7] data-[state=active]:bg-[#2f6046] data-[state=active]:text-white"
              >
                Results
              </TabsTrigger>
            </TabsList>

            <div className="text-xs text-[#4f6b5a]">
              {activeView === "layer-matcher" && hasRhinoLayers
                ? "Review Rhino layers and assign them to LCA categories."
                : activeView === "collect"
                ? "Shortlist EPDs and review saved wall buildups."
                : activeView === "build"
                ? "Add components and assign EPDs."
                : "Review totals, breakdowns, and stage impacts."}
            </div>
          </div>

          {hasRhinoLayers && (
            <TabsContent value="layer-matcher" className="space-y-6">
              <Card className="border border-[#d5dacc] bg-white shadow-sm rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#2b4331] text-base">Layer matcher</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-[#eef2e7] text-[#2b4331]">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Layer name</th>
                          <th className="px-3 py-2 text-left font-semibold">Declared unit</th>
                          <th className="px-3 py-2 text-left font-semibold">Quantity</th>
                          <th className="px-3 py-2 text-left font-semibold">Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {layerMatcherRows.map((row) => (
                          <tr key={row.id} className="border-b border-[#d5dacc] last:border-b-0">
                            <td className="px-3 py-2 text-[#2b4331]">{row.layer}</td>
                            <td className="px-3 py-2 text-[#2b4331]">{row.declaration}</td>
                            <td className="px-3 py-2 text-[#2b4331]">
                              {row.quantity !== "" ? row.quantity.toLocaleString() : "—"}
                            </td>
                            <td className="px-3 py-2">
                              <select
                                value={layerCategoryMap[row.id] || ""}
                                onChange={(e) => handleLayerCategoryChange(row.id, e.target.value)}
                                className="h-9 w-full rounded-md border border-[#d5dacc] bg-white px-2 text-sm text-[#2b4331] focus:outline-none focus:ring-2 focus:ring-[#2f6046]"
                              >
                                <option value="">Assign category</option>
                                {LAYER_CATEGORY_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 flex flex-col items-end gap-2">
                    <Button
                      type="button"
                      onClick={handleSaveLayerMatcher}
                      disabled={layerSaveState === "saving"}
                      className="bg-[#2f6046] text-white hover:bg-[#25503a] disabled:opacity-70"
                    >
                      {layerSaveState === "saving" ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : layerSaveState === "saved" ? (
                        <>
                          <Check className="mr-1 h-4 w-4" />
                          Saved
                        </>
                      ) : (
                        "Save"
                      )}
                    </Button>
                    {layerSaveNotice && (
                      <span className="text-xs text-[#a16207]">{layerSaveNotice}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="collect" className="space-y-6">
            <div className="w-full">
              <LibraryPanel epdLibrary={epdLibrary} setEpdLibrary={setEpdLibrary} />
            </div>
          </TabsContent>

          <TabsContent value="build" className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-[#4f6b5a]">
                Build components and save to update results and downstream dashboards.
              </div>
              <Button
                type="button"
                onClick={handleBuildSave}
                disabled={!buildDirty || buildSaveState === "saving"}
                className="h-8 bg-[#2f6046] text-white hover:bg-[#25503a] disabled:opacity-60"
              >
                {buildSaveState === "saving"
                  ? "Saving..."
                  : buildSaveState === "saved"
                  ? "Saved"
                  : "Save build"}
              </Button>
            </div>
            <div className="space-y-4 lg:space-y-5">
              {LCA_CATEGORIES.map((c, idx) => (
                <div key={c.key} className="space-y-4">
                  <CategorySection
                    categoryKey={c.key}
                    categoryLabel={c.label}
                    items={groupedByCategory[c.key]}
                    epdLibrary={epdLibrary}
                    computedByItemId={computedProject?.byItemId}
                    buildingArea={buildingArea}
                    buildingLifeYears={buildingLifeYears}
                    onAdd={addItem}
                    onUpdate={updateItem}
                    onRemove={removeItem}
                  />
                  {idx < LCA_CATEGORIES.length - 1 ? <Separator className="bg-[#d5dacc]" /> : null}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="results" className="space-y-6">
            <div className="space-y-4 lg:space-y-5">
              <ResultsPanel
                computedProject={computedProject}
                items={items}
                epdsById={epdsById}
                buildingArea={buildingArea}
                buildingLifeYears={buildingLifeYears}
              />
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <DialogContent className="max-w-md bg-white text-[#2b4331]">
          <DialogHeader>
            <DialogTitle>Clear all components?</DialogTitle>
            <DialogDescription className="text-[#4f6b5a]">
              This will remove all components from the current LCA build list. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-8 border-[#d5dacc] bg-white text-[#2b4331] hover:bg-[#eef2e7]"
              onClick={() => setClearConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-8 bg-[#2f6046] text-white hover:bg-[#25503a]"
              onClick={() => {
                clearAll();
                setClearConfirmOpen(false);
              }}
            >
              Clear all
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
